import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  FiPlus,
  FiShield,
  FiLayers,
  FiGlobe,
  FiDatabase,
  FiAward,
  FiZap,
  FiSettings,
  FiUsers,
  FiBarChart,
  FiCircle,
  FiLock,
  FiUnlock,
  FiCpu,
  FiGrid,
  FiPackage,
  FiTruck,
  FiRefreshCw,
  FiXCircle,
  FiMapPin
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useTokenization } from '../../contexts/TokenizationContext';
import AssetCreationWizard from './AssetCreationWizard';
import BulkSimilarAssetsModal from './BulkSimilarAssetsModal';
import TokenizationWizard from './TokenizationWizard';
import FractionalNFTWizard from './FractionalNFTWizard';
import AssetPortfolio from './AssetPortfolio';
import VehicleTokenizationWizard from '../VehicleTokenization/VehicleTokenizationWizard';
import { getStoredSession, subscribeToSessionChanges } from '../../api/authStorage';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import {
  allowedSectorEnumsFromIssuerProfile,
  isUiCategoryAllowedForTokenizer
} from '../../api/smartTokenizerMappers';

const TokenizerContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #4facfe, #00f2fe, #4facfe);
    animation: shimmer 3s infinite linear;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 50%, #4facfe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
  font-weight: 800;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.4rem;
  color: #a0a0a0;
  max-width: 800px;
  margin: 0 auto 2rem;
  line-height: 1.7;
`;

const HeroFeatures = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const HeroFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #4facfe;
  font-weight: 600;
  font-size: 1.1rem;

  .icon {
    font-size: 1.3rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: #4facfe;
    box-shadow: 0 20px 40px rgba(79, 172, 254, 0.15);
  }

  .value {
    font-size: 2.5rem;
    font-weight: 700;
    color: #4facfe;
    margin-bottom: 0.5rem;
  }

  .label {
    color: #a0a0a0;
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  .trend {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #00ff88;
    margin-top: 0.5rem;
  }
`;

const MainActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
`;

const TokenizationToolsSection = styled.section`
  margin-bottom: 3rem;
`;

const actionCardSurface = css`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: inherit;
  text-align: left;

  &:hover {
    transform: translateY(-5px);
    border-color: #4facfe;
    box-shadow: 0 20px 40px rgba(79, 172, 254, 0.15);
  }

  .icon {
    font-size: 3rem;
    color: #4facfe;
    margin-bottom: 1.5rem;
  }

  .title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 1rem;
  }

  .description {
    color: #a0a0a0;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .features {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .feature-tag {
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 12px;
    padding: 0.3rem 0.7rem;
    font-size: 0.8rem;
    color: #4facfe;
  }
`;

const ActionCard = styled(motion.button)`
  ${actionCardSurface}
`;

const MotionLink = motion(Link);

const ActionCardLink = styled(MotionLink)`
  ${actionCardSurface}
  text-decoration: none;
  display: block;
`;

const FlowStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 2rem;

  a {
    color: #9bd6ff;
    text-decoration: none;
    padding: 0.55rem 1.1rem;
    border: 1px solid rgba(79, 172, 254, 0.35);
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 500;

    &:hover {
      background: rgba(79, 172, 254, 0.12);
    }
  }
`;

const AssetsSection = styled.section`
  margin-bottom: 3rem;
`;

const PhysicalAssetOperationsSection = styled.section`
  margin-bottom: 3rem;
`;

const AssetsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
`;

const DemoNote = styled.p`
  text-align: center;
  color: #a0a0a0;
  font-size: 0.9rem;
  max-width: 900px;
  margin: 0 auto 2rem;
  line-height: 1.5;
`;

const CategoriesSection = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: #fff;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    color: #4facfe;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const CategoryCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid
    ${(p) => (p.$disabled ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.1)')};
  border-radius: 16px;
  padding: 2rem;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.38 : 1)};
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};
  filter: ${(p) => (p.$disabled ? 'grayscale(0.65)' : 'none')};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  ${(p) =>
    p.$disabled &&
    `
    &::after {
      content: 'Not authorized';
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      font-size: 0.65rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: rgba(255, 180, 180, 0.9);
      border: 1px solid rgba(255, 120, 120, 0.35);
      border-radius: 6px;
      padding: 0.2rem 0.45rem;
      background: rgba(80, 20, 20, 0.35);
      pointer-events: none;
      z-index: 1;
    }
  `}

  &:hover {
    transform: ${(p) => (p.$disabled ? 'none' : 'translateY(-3px)')};
    border-color: ${(p) => (p.$disabled ? 'rgba(255, 255, 255, 0.06)' : p.color)};
    box-shadow: ${(p) => (p.$disabled ? 'none' : `0 15px 30px ${p.color}20`)};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) => props.color};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: ${(p) => (p.$disabled ? 0 : 1)};
  }

  .header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .icon {
    font-size: 2.5rem;
  }

  .title {
    font-size: 1.3rem;
    font-weight: 700;
    color: #fff;
  }

  .description {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }

  .stats {
    display: flex;
    justify-content: between;
    gap: 1rem;
    font-size: 0.9rem;
  }

  .stat {
    color: #4facfe;
    
    .value {
      font-weight: 600;
    }
    
    .label {
      color: #a0a0a0;
    }
  }
`;

const GatePanel = styled.div`
  max-width: 520px;
  margin: 3rem auto 0;
  padding: 2rem;
  background: rgba(255, 100, 100, 0.08);
  border: 1px solid rgba(255, 100, 100, 0.25);
  border-radius: 16px;
  color: #fecaca;
  text-align: center;
  line-height: 1.6;

  svg {
    display: block;
    margin: 0 auto 1rem;
    font-size: 2.5rem;
    color: #f87171;
  }

  h2 {
    color: #fff;
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
    font-weight: 700;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: ${(props) =>
    props.isVehicleModal ? '1200px' : props.isBulkSimilarModal ? '960px' : '800px'};
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

function CoreTokenizer() {
  const {
    statistics,
    categories,
    tokenTypes,
    assets,
    tokenizations,
    getAssetsByCategory,
    loading
  } = useTokenization();

  const { currentIssuer, isAdmin, isAuthorizedTokenizer } = useTokenIssuer();
  const sectorGateActive = isAuthorizedTokenizer && !isAdmin;
  const allowedSectorEnums = useMemo(() => {
    if (!sectorGateActive) return null;
    return allowedSectorEnumsFromIssuerProfile(currentIssuer);
  }, [sectorGateActive, currentIssuer]);

  const [hasSession, setHasSession] = useState(() => Boolean(getStoredSession()?.accessToken));
  const [activeModal, setActiveModal] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [tokenizeInitialAssetId, setTokenizeInitialAssetId] = useState(null);

  useEffect(() => {
    setHasSession(Boolean(getStoredSession()?.accessToken));
    return subscribeToSessionChanges((session) => {
      setHasSession(Boolean(session?.accessToken));
    });
  }, []);

  useEffect(() => {
    if (!hasSession) {
      setActiveModal(null);
      setSelectedCategory(null);
    }
  }, [hasSession]);

  // Check URL parameters to auto-open modals (signed-in users only)
  useEffect(() => {
    if (!hasSession) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const modalParam = urlParams.get('modal');
    const tokenizeAssetParam = urlParams.get('tokenizeAsset');

    if (tokenizeAssetParam) {
      setTokenizeInitialAssetId(tokenizeAssetParam);
      setActiveModal('tokenizeAsset');
    } else if (modalParam === 'vehicle') {
      setActiveModal('vehicleTokenization');
    }

    if (modalParam || tokenizeAssetParam) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [hasSession]);

  // Modal handlers
  const openModal = (modalType, category = null) => {
    setSelectedCategory(category);
    setActiveModal(modalType);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedCategory(null);
    setTokenizeInitialAssetId(null);
  };

  // Calculate category statistics
  const getCategoryStats = (categoryId) => {
    const categoryAssets = getAssetsByCategory(categoryId);
    const tokenizedAssets = categoryAssets.filter(
      (asset) => asset.status === 'Minted' || asset.status === 'tokenized' || asset.tokenizationId
    );
    const totalValue = categoryAssets.reduce((sum, asset) => sum + (asset.valuationUSD || 0), 0);
    
    return {
      totalAssets: categoryAssets.length,
      tokenizedAssets: tokenizedAssets.length,
      totalValue
    };
  };

  if (!hasSession) {
    return (
      <TokenizerContainer>
        <HeroSection>
          <HeroTitle>Core Tokenizer</HeroTitle>
          <HeroSubtitle>
            The Ultimate Real-World Asset Tokenization Platform. Transform any physical or digital asset into
            blockchain tokens with full regulatory compliance and institutional-grade security.
          </HeroSubtitle>
          <GatePanel>
            <FiLock />
            <h2>Sign in required</h2>
            <p>
              Tokenization tools, asset wizards, and portfolio actions are available after you sign in. Use the{' '}
              <strong>Login</strong> button in the header to continue.
            </p>
          </GatePanel>
        </HeroSection>
      </TokenizerContainer>
    );
  }

  return (
    <TokenizerContainer>
      <HeroSection>
        <HeroTitle>Core Tokenizer</HeroTitle>
        <HeroSubtitle>
          The Ultimate Real-World Asset Tokenization Platform. Transform any physical or digital asset 
          into blockchain tokens with full regulatory compliance and institutional-grade security.
        </HeroSubtitle>
        <HeroFeatures>
          <HeroFeature>
            <FiShield className="icon" />
            Core Blockchain Native
          </HeroFeature>
          <HeroFeature>
            <FiGlobe className="icon" />
            Global Compliance
          </HeroFeature>
          <HeroFeature>
            <FiLayers className="icon" />
            Multi-Token Standards
          </HeroFeature>
          <HeroFeature>
            <FiZap className="icon" />
            Instant Settlement
          </HeroFeature>
        </HeroFeatures>
      </HeroSection>

      <FlowStrip>
        {(isAuthorizedTokenizer || isAdmin) && <Link to="/my-assets">Assets Registry</Link>}
        {(isAuthorizedTokenizer || isAdmin) && <Link to="/bulk-assets-registry">Bulk assets registry</Link>}
        {(isAuthorizedTokenizer || isAdmin) && (
          <Link to="/tokenization/offline-mint">Offline mint (deployed contracts)</Link>
        )}
        <Link to="/asset-classes">Asset classes & batch mint</Link>
        <Link to="/legal-reviews">Legal review (API)</Link>
        <Link to="/blockchain-explorer">Transactions & contract reads</Link>
      </FlowStrip>

      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="value">{statistics.totalAssets.toLocaleString()}</div>
          <div className="label">Total Assets Registered</div>
          <div className="trend" style={{ color: '#64748b', fontSize: '0.82rem' }}>
            From loaded registry
          </div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="value">
            {statistics.totalValue >= 1_000_000
              ? `$${(statistics.totalValue / 1_000_000).toFixed(2)}M`
              : statistics.totalValue >= 1_000
                ? `$${(statistics.totalValue / 1_000).toFixed(1)}K`
                : `$${Math.round(statistics.totalValue).toLocaleString()}`}
          </div>
          <div className="label">Total Assets Value</div>
          <div className="trend" style={{ color: '#64748b', fontSize: '0.82rem' }}>
            Valuation (USD) sum
          </div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="value">{statistics.activeTokenizations.toLocaleString()}</div>
          <div className="label">Active Tokenizations</div>
          <div className="trend" style={{ color: '#64748b', fontSize: '0.82rem' }}>
            Active in this session
          </div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="value">{statistics.fractionalizedAssets.toLocaleString()}</div>
          <div className="label">Fractionalized Assets</div>
          <div className="trend" style={{ color: '#64748b', fontSize: '0.82rem' }}>
            Status: Fractionalized
          </div>
        </StatCard>
      </StatsGrid>

      <AssetsSection aria-labelledby="tokenizer-assets-heading">
        <SectionTitle id="tokenizer-assets-heading">
          <FiDatabase className="icon" />
          Assets
        </SectionTitle>
        <AssetsGrid>
          <ActionCard
            type="button"
            onClick={() => openModal('createAsset')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiPlus className="icon" />
            <div className="title">Create Asset</div>
            <div className="description">
              Register and verify a new real-world asset for tokenization. Upload documentation, set metadata, and
              prepare for blockchain deployment.
            </div>
            <div className="features">
              <span className="feature-tag">KYC Integration</span>
              <span className="feature-tag">Document Verification</span>
              <span className="feature-tag">Metadata Templates</span>
            </div>
          </ActionCard>
          {(isAuthorizedTokenizer || isAdmin) && (
            <ActionCard
              type="button"
              onClick={() => openModal('bulkSimilarAssets')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiPackage className="icon" />
              <div className="title">Create Bulk Asset</div>
              <div className="description">
                Register many similar assets in one flow: serial ranges, shared documents, and a single legal review
                before individual tokenization.
              </div>
              <div className="features">
                <span className="feature-tag">Batch registration</span>
                <span className="feature-tag">Shared documents</span>
                <span className="feature-tag">Legal review</span>
              </div>
            </ActionCard>
          )}
          {(isAuthorizedTokenizer || isAdmin) && (
            <ActionCardLink
              to="/my-assets"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiDatabase className="icon" />
              <div className="title">Assets Registry</div>
              <div className="description">
                Browse and manage your registered assets, track status from draft through legal review to mint-ready.
              </div>
              <div className="features">
                <span className="feature-tag">Your registry</span>
                <span className="feature-tag">Status & metadata</span>
                <span className="feature-tag">Documents</span>
              </div>
            </ActionCardLink>
          )}
          {(isAuthorizedTokenizer || isAdmin) && (
            <ActionCardLink
              to="/bulk-assets-registry"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiCpu className="icon" />
              <div className="title">Bulk Asset Registry</div>
              <div className="description">
                Registry of bulk batches; tokenize when all members are approved, with ERC-721 class contracts.
              </div>
              <div className="features">
                <span className="feature-tag">Bulk batches</span>
                <span className="feature-tag">Approval gate</span>
                <span className="feature-tag">ERC-721</span>
              </div>
            </ActionCardLink>
          )}
          <ActionCardLink
            to="/asset-classes"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiGrid className="icon" />
            <div className="title">Asset Class Manager</div>
            <div className="description">
              Configure asset classes, metadata schemas, contract deployment, and batch minting—the same tool
              available from the flow strip above.
            </div>
            <div className="features">
              <span className="feature-tag">Templates</span>
              <span className="feature-tag">Deploy & mint</span>
              <span className="feature-tag">ERC standards</span>
            </div>
          </ActionCardLink>
          <ActionCard
            type="button"
            onClick={() => openModal('portfolio')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiDatabase className="icon" />
            <div className="title">Asset Portfolio</div>
            <div className="description">
              Manage your complete tokenized asset portfolio. Monitor performance, track ownership, and execute
              advanced operations.
            </div>
            <div className="features">
              <span className="feature-tag">Real-time Analytics</span>
              <span className="feature-tag">Ownership Tracking</span>
              <span className="feature-tag">Performance Metrics</span>
            </div>
          </ActionCard>
        </AssetsGrid>
      </AssetsSection>

      <TokenizationToolsSection aria-labelledby="tokenizer-tools-heading">
        <SectionTitle id="tokenizer-tools-heading">
          <FiLayers className="icon" />
          Tokenization
        </SectionTitle>
        <MainActions>
        <ActionCard
          onClick={() => openModal('tokenizeAsset')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiLayers className="icon" />
          <div className="title">Tokenize Asset</div>
          <div className="description">
            Convert verified assets into fungible tokens (FT), non-fungible tokens (NFT), 
            or create fractional ownership structures.
          </div>
          <div className="features">
            <span className="feature-tag">CBC20/CBC721/CBC1155</span>
            <span className="feature-tag">Core Contracts</span>
            <span className="feature-tag">Instant Deployment</span>
          </div>
        </ActionCard>

        <ActionCard
          onClick={() => openModal('fractionalNFT')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiUnlock className="icon" />
          <div className="title">Fractionalize NFT</div>
          <div className="description">
            Split unique high-value assets into tradeable fractions, enabling retail access 
            to premium real estate, art, and collectibles.
          </div>
          <div className="features">
            <span className="feature-tag">Core Vaults</span>
            <span className="feature-tag">Redemption Logic</span>
            <span className="feature-tag">Dynamic Pricing</span>
          </div>
        </ActionCard>

        <ActionCard
          onClick={() => openModal('vehicleTokenization')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiCircle className="icon" />
          <div className="title">Vehicle Tokenization</div>
          <div className="description">
            Professional CIP-227 composable vehicle NFTs for McLaren and luxury automotive assets. 
            Tokenize vehicles and individual parts with full composability support.
          </div>
          <div className="features">
            <span className="feature-tag">CIP-227 Composable</span>
            <span className="feature-tag">McLaren Partnership</span>
            <span className="feature-tag">Part Management</span>
          </div>
        </ActionCard>
        </MainActions>
      </TokenizationToolsSection>

      <DemoNote>
        Backend-backed issuance today: create asset (POST + documents + submit for legal), legal review workflow,
        asset class deploy + offline mint (Tokenize Asset & batch mint), asset update (PUT), and mint requests
        (prepare + submit). Fractional NFT and vehicle wizards remain UI demos until matching APIs exist.
      </DemoNote>

      {isAuthorizedTokenizer && (
        <PhysicalAssetOperationsSection aria-labelledby="physical-asset-ops-heading">
          <SectionTitle id="physical-asset-ops-heading">
            <FiMapPin className="icon" />
            Physical Asset Operations
          </SectionTitle>
          <AssetsGrid>
            <ActionCardLink
              to="/custodial-operations"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiRefreshCw className="icon" />
              <div className="title">Token Redemption</div>
              <div className="description">
                Custody and settlement workflows for processing redemptions, releases, and operational controls tied to
                tokenized assets.
              </div>
              <div className="features">
                <span className="feature-tag">Redemption flow</span>
                <span className="feature-tag">Settlement</span>
                <span className="feature-tag">Custody</span>
              </div>
            </ActionCardLink>
            <ActionCardLink
              to="/inventory-management"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiTruck className="icon" />
              <div className="title">Physical Delivery</div>
              <div className="description">
                Vaulted inventory and reconciliation views to support physical delivery, positions, and logistics
                handoffs.
              </div>
              <div className="features">
                <span className="feature-tag">Inventory</span>
                <span className="feature-tag">Vaulted assets</span>
                <span className="feature-tag">Logistics</span>
              </div>
            </ActionCardLink>
            <ActionCardLink
              to="/compliance-center"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiXCircle className="icon" />
              <div className="title">Asset Nullification</div>
              <div className="description">
                Compliance and policy tools for retiring representations, attestations, and controlled wind-down of
                asset records where applicable.
              </div>
              <div className="features">
                <span className="feature-tag">Policies</span>
                <span className="feature-tag">Attestation</span>
                <span className="feature-tag">Lifecycle</span>
              </div>
            </ActionCardLink>
            <ActionCardLink
              to="/warehouse-operations"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiPackage className="icon" />
              <div className="title">Warehouse Operations</div>
              <div className="description">
                Warehouse logistics, receipts, and on-the-ground handling aligned with physical asset programs.
              </div>
              <div className="features">
                <span className="feature-tag">Receipts</span>
                <span className="feature-tag">Handling</span>
                <span className="feature-tag">Operations</span>
              </div>
            </ActionCardLink>
          </AssetsGrid>
        </PhysicalAssetOperationsSection>
      )}

      <CategoriesSection>
        <SectionTitle>
          <FiCircle className="icon" />
          Asset Categories
        </SectionTitle>
        <CategoriesGrid>
          {Object.values(categories).map((category, index) => {
            const stats = getCategoryStats(category.id);
            const catAllowed = isUiCategoryAllowedForTokenizer(category.id, allowedSectorEnums);
            return (
              <CategoryCard
                key={category.id}
                color={category.color}
                $disabled={!catAllowed}
                role="button"
                tabIndex={catAllowed ? 0 : -1}
                aria-disabled={!catAllowed}
                onClick={() => {
                  if (!catAllowed) return;
                  openModal('createAsset', category);
                }}
                onKeyDown={(e) => {
                  if (!catAllowed) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal('createAsset', category);
                  }
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={catAllowed ? { scale: 1.02 } : {}}
                whileTap={catAllowed ? { scale: 0.98 } : {}}
              >
                <div className="header">
                  <span className="icon">{category.icon}</span>
                  <span className="title">{category.name}</span>
                </div>
                <div className="description">{category.description}</div>
                <div className="stats">
                  <div className="stat">
                    <span className="value">{stats.totalAssets}</span> <span className="label">assets</span>
                  </div>
                  <div className="stat">
                    <span className="value">{stats.tokenizedAssets}</span> <span className="label">tokenized</span>
                  </div>
                  <div className="stat">
                    <span className="value">${(stats.totalValue / 1000).toFixed(0)}K</span> <span className="label">value</span>
                  </div>
                </div>
              </CategoryCard>
            );
          })}
        </CategoriesGrid>
      </CategoriesSection>

      {/* Modals */}
      <AnimatePresence>
        {activeModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              isVehicleModal={activeModal === 'vehicleTokenization'}
              isBulkSimilarModal={activeModal === 'bulkSimilarAssets'}
            >
              {activeModal === 'createAsset' && (
                <AssetCreationWizard 
                  onClose={closeModal} 
                  selectedCategory={selectedCategory}
                />
              )}
              {activeModal === 'bulkSimilarAssets' && <BulkSimilarAssetsModal onClose={closeModal} />}
              {activeModal === 'tokenizeAsset' && (
                <TokenizationWizard onClose={closeModal} initialAssetId={tokenizeInitialAssetId} />
              )}
              {activeModal === 'fractionalNFT' && (
                <FractionalNFTWizard onClose={closeModal} />
              )}
              {activeModal === 'portfolio' && (
                <AssetPortfolio onClose={closeModal} />
              )}
              {activeModal === 'vehicleTokenization' && (
                <VehicleTokenizationWizard onClose={closeModal} />
              )}
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </TokenizerContainer>
  );
}

export default CoreTokenizer; 